// Dedicated explanation pages behind the three landing value cards:
// /tariff-optimization, /battery-arbitrage, /energy-intelligence.
// Engineering tone — "Energy Intelligence", never "AI assistant".
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { FileText, Lightbulb, PiggyBank, TrendingDown, TrendingUp } from "../../components/icons";
import { LandingShell } from "./LandingShell";

type TopicKey = "tariff" | "arbitrage" | "intelligence";

// Contextual background photo per topic (see BgPhotos / LandingShell).
const TOPIC_BG: Record<TopicKey, string> = {
  tariff: "energy",
  arbitrage: "energy",
  intelligence: "hardware",
};

function ValueDetail({
  topic,
  icon,
  tone,
  extra,
}: {
  topic: TopicKey;
  icon: ReactNode;
  tone: string;
  extra?: ReactNode;
}) {
  const { t } = useTranslation();
  const points = ["p1", "p2", "p3"] as const;
  return (
    <LandingShell>
      <div className="max-w-3xl mx-auto px-4 py-12" data-bg={TOPIC_BG[topic]}>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-text transition">
          <ArrowLeft size={14} />
          {t("valuePages.backHome")}
        </Link>
        <div className={`mt-6 inline-flex items-center gap-2 text-sm font-medium ${tone}`}>
          {icon}
          {t(`landing.value.${topic}.title`)}
        </div>
        <h1 className="text-3xl md:text-4xl font-semibold mt-2">
          {t(`landing.value.${topic}.amount`)}
        </h1>
        <p className="text-muted mt-5 text-base leading-relaxed">
          {t(`valuePages.${topic}.intro`)}
        </p>

        <div className="mt-8 space-y-4">
          {points.map((p, i) => (
            <div key={p} className="card p-5 flex items-start gap-4">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-panel2 border border-border text-sm font-semibold shrink-0">
                {i + 1}
              </span>
              <div>
                <h3 className="font-semibold text-sm">{t(`valuePages.${topic}.${p}t`)}</h3>
                <p className="text-sm text-muted mt-1">{t(`valuePages.${topic}.${p}`)}</p>
              </div>
            </div>
          ))}
        </div>

        {extra}

        <p className="text-xs text-muted mt-8 border-t border-border pt-4">
          {t(`valuePages.${topic}.disclaimer`)}
        </p>

        <div className="mt-8 flex items-center gap-3">
          <Link to="/register" className="btn-primary">{t("landing.ctaPrimary")}</Link>
          <Link to="/#pricing" className="btn-ghost">{t("nav.pricing")}</Link>
        </div>
      </div>
    </LandingShell>
  );
}

export function TariffOptimizationPage() {
  return (
    <ValueDetail topic="tariff" icon={<PiggyBank size={18} />} tone="text-good" />
  );
}

export function BatteryArbitragePage() {
  return (
    <ValueDetail topic="arbitrage" icon={<TrendingUp size={18} />} tone="text-brand" />
  );
}

function SampleReport() {
  const { t } = useTranslation();
  const lines = [
    { icon: <TrendingUp size={15} className="mt-0.5 shrink-0" />, text: t("valuePages.intelligence.sample1") },
    { icon: <TrendingDown size={15} className="mt-0.5 shrink-0" />, text: t("valuePages.intelligence.sample2") },
    { icon: <Lightbulb size={15} className="mt-0.5 shrink-0" />, text: t("valuePages.intelligence.sample3") },
  ];
  return (
    <div className="card p-5 mt-8">
      <h3 className="text-sm font-semibold flex items-center gap-2 mb-4">
        <FileText size={15} />
        {t("valuePages.intelligence.sampleTitle")}
      </h3>
      <ul className="space-y-3">
        {lines.map((line, i) => (
          <li key={i} className="flex items-start gap-3 text-sm">
            {line.icon}
            <span>{line.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function EnergyIntelligencePage() {
  return (
    <ValueDetail
      topic="intelligence"
      icon={<FileText size={18} />}
      tone="text-accent"
      extra={<SampleReport />}
    />
  );
}
